import React, { FC, useRef } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import translate from '@translate';
import { colors, sizes } from 'src/themes/default';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import { ClaimProductType } from '../../types/claim';
import { FormErrorsType } from '../../types/global';
import ProductNameLink from 'src/components/ProductNameLink';
import {
    isEmptyProductQuantityInput,
    ProductQuantityInputValue,
    shouldApplyNonNegativeProductQuantityInput,
    shouldApplyProductQuantityInput,
    shouldRestoreNonNegativeProductQuantityOnBlur,
    shouldRestoreProductQuantityOnBlur,
    toProductQuantityInputValue,
} from 'src/utils/productQuantityInput';

export type EditKeysType = keyof ClaimProductType;

const allColumns: TableColumnType[] = [
    {
        key: 'SKU',
        label: 'sku'
    },
    {
        key: 'NAME',
        label: 'name',
        customRender: true
    },
    {
        key: 'CLAIMED_QUANTITY',
        label: 'claimedQuantity',
        type: 'number-filter',
        customRender: true
    },
    {
        key: 'RESEND_QUANTITY',
        label: 'resendQuantity',
        type: 'number-filter',
        customRender: true
    }
];

export interface ProductsTablePropsType {
    data?: ClaimProductType[];
    newClaim: boolean;
    onChange: (value: any, error: boolean, errorKey?: string) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
    skladonUser?: number;
    canEditProducts: boolean;
}

const ProductsTable: FC<ProductsTablePropsType> = (props) => {
    const getInputName = (id: number, key: string) => `product-${id}-${key}`;
    const quantityOnFocusRef = useRef<Record<string, number | null>>({});
    const lastQuantityInputRef = useRef<Record<string, ProductQuantityInputValue>>({});

    const changeValue = (key: EditKeysType, value: any, error: boolean, id: number) => {
        const newData = props.data?.map((product) => {
            if (product.ID === id) {
                const updatedProduct = { ...product };
                updatedProduct[key] = value;
                return updatedProduct;
            }
            return product;
        });
        props.onChange(newData, error, getInputName(id, key));
    };

    const handleClaimedQuantityFocus = (name: string, currentQuantity: number) => {
        quantityOnFocusRef.current[name] = currentQuantity;
        lastQuantityInputRef.current[name] = currentQuantity;
    };

    const handleClaimedQuantityChange = (
        value: string | number | undefined | null,
        error: boolean,
        id: number,
        name: string
    ) => {
        const inputValue = toProductQuantityInputValue(value);
        lastQuantityInputRef.current[name] = inputValue;
        if (!shouldApplyProductQuantityInput(inputValue)) {
            return;
        }
        changeValue('CLAIMED_QUANTITY', inputValue, error, id);
    };

    const handleClaimedQuantityBlur = (id: number, name: string) => {
        const lastValue = lastQuantityInputRef.current[name] ?? quantityOnFocusRef.current[name] ?? 'empty';
        if (!shouldRestoreProductQuantityOnBlur(lastValue)) {
            return;
        }
        const quantityOnFocus = quantityOnFocusRef.current[name] ?? 0;
        changeValue('CLAIMED_QUANTITY', quantityOnFocus, false, id);
    };

    const handleResendQuantityFocus = (name: string, currentQuantity: number | null) => {
        quantityOnFocusRef.current[name] = currentQuantity;
        lastQuantityInputRef.current[name] =
            currentQuantity === null || currentQuantity === undefined ? 'empty' : currentQuantity;
    };

    const handleResendQuantityChange = (
        value: string | number | undefined | null,
        error: boolean,
        id: number,
        name: string
    ) => {
        if (isEmptyProductQuantityInput(value)) {
            lastQuantityInputRef.current[name] = 'empty';
            changeValue('RESEND_QUANTITY', null, error, id);
            return;
        }
        const inputValue = toProductQuantityInputValue(value);
        lastQuantityInputRef.current[name] = inputValue;
        if (!shouldApplyNonNegativeProductQuantityInput(inputValue)) {
            return;
        }
        changeValue('RESEND_QUANTITY', inputValue, error, id);
    };

    const handleResendQuantityBlur = (id: number, name: string) => {
        const lastValue = lastQuantityInputRef.current[name] ?? 'empty';
        if (!shouldRestoreNonNegativeProductQuantityOnBlur(lastValue)) {
            return;
        }
        changeValue('RESEND_QUANTITY', quantityOnFocusRef.current[name] ?? null, false, id);
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        const disabledRow = row.LINE_STATE === 'D';
        switch (column.key) {
            case 'NAME':
                return <ProductNameLink productId={row.PRODUCT_ID} name={row.NAME} />;
            case 'CLAIMED_QUANTITY':
                return (
                    <TextFieldDebounced
                        value={row.CLAIMED_QUANTITY}
                        error={props.editErrors[name]}
                        onChange={(value, error) => handleClaimedQuantityChange(value, error, row.ID, name)}
                        onFocus={() => handleClaimedQuantityFocus(name, Number(row.CLAIMED_QUANTITY || 0))}
                        onBlur={() => handleClaimedQuantityBlur(row.ID, name)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={1}
                        step="1"
                        required={!disabledRow && !props.newClaim}
                        disabled={props.disableEdit || disabledRow}
                    />
                );
            case 'RESEND_QUANTITY':
                return (
                    <TextFieldDebounced
                        value={row.RESEND_QUANTITY}
                        error={props.editErrors[name]}
                        onChange={(value, error) => handleResendQuantityChange(value, error, row.ID, name)}
                        onFocus={() =>
                            handleResendQuantityFocus(
                                name,
                                row.RESEND_QUANTITY === null || row.RESEND_QUANTITY === undefined
                                    ? null
                                    : Number(row.RESEND_QUANTITY)
                            )
                        }
                        onBlur={() => handleResendQuantityBlur(row.ID, name)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        step="1"
                        disabled={props.disableEdit || disabledRow || !props.skladonUser}
                    />
                );
            default:
                return null;
        }
    };

    const handleToggleRow = (row: TableRowType) => {
        const newLineState = row.LINE_STATE === 'D' ? 'E' : 'D';

        if (row.ID < 0) {
            if (row.LINE_STATE === 'D') {
                props.onChange(
                    props.data?.map((item) => {
                        if (item.ID === row.ID) {
                            return { ...item, LINE_STATE: 'E' };
                        }
                        return item;
                    }),
                    false
                );
            } else {
                props.onChange(
                    props.data?.filter((item) => item.ID != row.ID),
                    false
                );
            }
        } else {
            props.onChange(
                props.data?.map((item) => {
                    if (item.ID === row.ID) {
                        return { ...item, LINE_STATE: newLineState };
                    }
                    return item;
                }),
                false
            );
        }
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="claimDetail.productTable"
            allColumns={allColumns}
            initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
            rowActions={[
                {
                    icon: <HighlightOffIcon />,
                    label: translate('returnDetail.productTable.action.removeProduct'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'D' || !props.canEditProducts,
                    style: { height: sizes.table_action_with_input_H }
                },
                {
                    icon: <AddCircleIcon />,
                    label: translate('returnDetail.productTable.action.addProduct'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'E' || !props.canEditProducts,
                    style: { height: sizes.table_action_with_input_H }
                }
            ]}
            customRender={customRender}
            rowSxFce={(row) =>
                row.LINE_STATE === 'D'
                    ? { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } }
                    : {}
            }
            disableFilter
            maxHeight={'unset'}
            sumCols={{ CLAIMED_QUANTITY: true, RESEND_QUANTITY: true }}
            sumColsFilter={(row) => row.LINE_STATE === 'E'}
        />
    );
};

export default ProductsTable;
